import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi } from '../shizaiApi';
import { LineCard } from './LineCard';
import { DeliveryDestPicker } from './DeliveryDestPicker';
import { emptyLine, newKey, validateLines, toLineBody } from './shared';

/** 依頼の編集 — used by both マイリクエスト (owner) and 受信依頼 (PIC). */
export function EditRequestDialog({ req, categories, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    needed_by: req.needed_by || '',
    priority: req.priority || 'normal',
    order_name: req.order_name || '',
    order_category_id: req.order_category_id || '',
    delivery_destination: req.delivery_destination?.id ? req.delivery_destination : null,
    is_tax_inclusive: !!req.is_tax_inclusive,
    notes: req.notes || '',
    lines: (req.lines || []).map((ln) => ({ ...emptyLine(), ...ln, _key: newKey(), qty: ln.qty ?? 1 })),
  }));
  const [saving, setSaving] = useState(false);

  const setLine = (i, patch) => setForm((f) => ({
    ...f,
    lines: f.lines.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln)),
  }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    const v = validateLines(form.lines);
    if (v.error) { toast.error(v.error); return; }
    setSaving(true);
    try {
      await shizaiApi.updateMyRequest(req.id, {
        needed_by: form.needed_by || null,
        priority: form.priority,
        order_name: form.order_name || null,
        order_category_id: form.order_category_id || null,
        delivery_destination_id: form.delivery_destination?.id || null,
        mfg_order_id: req.mfg_order_id || null,
        is_tax_inclusive: !!form.is_tax_inclusive,
        notes: form.notes || null,
        lines: v.lines.map(toLineBody),
      });
      toast.success(`依頼 ${req.request_no} を更新しました`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pr-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <PencilSimple size={16} weight="duotone" className="text-sky-600" />
            依頼を編集 — <span className="font-mono text-sm text-zinc-500">{req.request_no}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-600">希望納期</Label>
            <div className="mt-1">
              <DateField value={form.needed_by} onChange={(v) => setForm((f) => ({ ...f, needed_by: v }))} testId="pr-edit-needed-by" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">優先度</Label>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="mt-1 h-9" data-testid="pr-edit-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">低</SelectItem>
                <SelectItem value="normal">通常</SelectItem>
                <SelectItem value="high">高</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">受注品名</Label>
            <Input value={form.order_name} onChange={(e) => setForm((f) => ({ ...f, order_name: e.target.value }))}
              placeholder="例: SS-45BMC" className="mt-1 h-9" data-testid="pr-edit-order-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">受注分類</Label>
            <Select value={form.order_category_id || '__none__'}
              onValueChange={(v) => setForm((f) => ({ ...f, order_category_id: v === '__none__' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-9" data-testid="pr-edit-category"><SelectValue placeholder="選択してください" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="__none__">（未選択）</SelectItem>
                {(categories || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.symbol ? ` (${c.symbol})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">納入先</Label>
            <DeliveryDestPicker value={form.delivery_destination}
              onChange={(dd) => setForm((f) => ({ ...f, delivery_destination: dd }))}
              testId="pr-edit-delivery-dest" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold text-zinc-600">全体備考</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2} className="mt-1" data-testid="pr-edit-notes" />
          </div>
        </div>

        <div className="space-y-3">
          {form.lines.map((ln, i) => (
            <LineCard
              key={ln._key}
              line={ln}
              index={i}
              canRemove={form.lines.length > 1}
              onChange={(patch) => setLine(i, patch)}
              onRemove={() => removeLine(i)}
            />
          ))}
          <Button variant="outline" size="sm" onClick={addLine} data-testid="pr-edit-add-line">
            <Plus size={14} className="mr-1" /> 品目を追加
          </Button>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="pr-edit-cancel">キャンセル</Button>
          <Button onClick={save} disabled={saving}
            className="bg-teal-700 text-white"
            data-testid="pr-edit-save">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
